import React, { FC, useEffect, useContext, useState } from "react"
import BusinessLink from "./BusinessLink"
import CodeOfEthicsModal from "../../components/CodeOfEthicsModal"
import Loader from "../../components/ui/loader"
import OnboardingCallModal from "../../components/OnboardingCallModal"
import useLoading from "../../hooks/useLoading"
import useScrollToTop from "../../hooks/useScrollToTop"
import { Button } from "../../components/ui/button"
import { ProfileRequests, UserInfo, VerifyPhone } from "../../api/app.service"
import { CommonFunctionCtx } from "../../context/commonFunctionContext"
import { SubscriptionCtx } from "../../context/subscriptionContext"
import {
  SvgBilling,
  SvgBusiness,
  SvgCalendar,
  SvgChat,
  SvgCredentials,
  SvgPhone,
  SvgUser3,
} from "../../components/icons/"
import { useLocation, useNavigate } from "react-router-dom"
import { UserCtx } from "../../context/userContext"
import { useToast } from "../../hooks/useToast"

interface Props {}

declare global {
  interface Window {
    Calendly: {
      initPopupWidget(config: { url: string }): void
    }
  }
}

const ManageBusiness: FC<Props> = () => {
  const { user } = useContext(UserCtx)
  const location = useLocation()
  const [businessLinks, setBusinessLinks] = useState<any[]>([])
  const [verifyPhoneForm, setVerifyPhoneForm] = useState<boolean>(false)
  const [tryProfileFeature, setTryProfileFeature] = useState<boolean>(false)
  const [profileAccessStatus, setProfileAccessStatus] =
    useState<string>("blocked")
  const [sentCode, setSentCode] = useState<boolean>(false)
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false)
  const { updateNextStepsData } = useContext(CommonFunctionCtx)
  const { toast } = useToast()
  const { startLoading, stopLoading } = useLoading()
  const { setUserActiveSubscription, activeSubscription } =
    useContext(SubscriptionCtx)
  const [coachPublicProfileData, setCoachPublicProfileData] = useState<any>()
  const navigate = useNavigate()

  const getCoachPrivateProfile = async () => {
    if (user?.coachProfile) {
      startLoading()
      await ProfileRequests.getCoachPrivateProfile({
        coach_profile_id: parseInt(user.coachProfile.profile_id),
      })
        .then((data) => {
          setCoachPublicProfileData(data)
        })
        .catch((ex) => {
          console.log(ex)
          toast.error(ex.response.data.message)
        })
        .finally(() => {
          stopLoading()
        })
    }
  }

  const sendVerifyPhone = () => {
    toast.clearAll()
    if (!sentCode) {
      VerifyPhone.sendPhoneCode({ access_token: user.access_token })
        .then((data) => {
          setSentCode(true)
          toast.success("Your email address is verified!")
          data.message === "complete" && setVerifyPhoneForm(true)
        })
        .catch((ex) => {
          console.log(ex)
          toast.error(ex.response.data.message)
          stopLoading()
        })
    }
  }

  if (["wrong-code", "no-code"].includes(location?.state?.message)) {
    toast.error(
      "Email verification failed. Please try again or contact support."
    )
    navigate(location.pathname, { replace: true })
  }

  const populateBusinessLinks = (nextStepsData: any) => {
    const communicationHubTitle =
      user?.organization === "BBHC"
        ? "Client Communication Hub"
        : "My Communication Hub"
    const businessLinksArray = [
      {
        title: "My Calendar",
        text: "Connect your calendar to manage your availability, schedule, and upcoming sessions.",
        link: "/account-settings/calendars",
        linkId: "calendars",
        complete: nextStepsData?.is_calendar_connect,
        step: 1,
        icon: <SvgCalendar />,
      },
      {
        title: "My Services",
        text: "AKA: The heart of your business. This is where you’ll load in the services, programs, and packages you offer clients.",
        link: "/coach/services",
        linkId: "services",
        complete: nextStepsData?.has_created_services,
        step: 2,
        icon: <SvgBusiness />,
      },
      {
        title: "My Zoee Website",
        text: "Go ahead, show off a little bit. This is where you’ll create and make updates to your Zoee website.",
        link: "/coach/edit-website",
        linkId: "profile",
        complete: profileAccessStatus === "approved" ? 1 : 0,
        step: 3,
        profileAccessStatus: profileAccessStatus,
        icon: <SvgCredentials />,
      },
      {
        title: "Payment Center",
        text: "This is where you'll view and manage your Zoee payment methods and transactions.",
        link: "/account-settings/payment-center",
        linkId: "payments",
        // complete: "",
        icon: <SvgBilling />,
      },
      {
        title: "My Contacts",
        text: "Get ready to send some invitations! Once your clients are in Zoee, you'll be off to the races.",
        link: "/coach/contacts",
        linkId: "contacts",
        complete: nextStepsData?.has_sent_invites,
        icon: <SvgUser3 />,
      },
      {
        title: communicationHubTitle,
        text: "This is where the fun starts. Go here to chat with your clients.",
        link: "/coach/inbox",
        linkId: "inbox",
        complete: nextStepsData?.has_sent_chats,
        icon: <SvgChat />,
      },
    ]
    setBusinessLinks(businessLinksArray)
  }

  const getNextStepsValues = async () => {
    await UserInfo.getNextStepsValues({
      user_id: parseInt(user.user_id),
    })
      .then((data) => {
        populateBusinessLinks(data.next_steps_data)
        setProfileAccessStatus(data.next_steps_data.profile_access_status)
      })
      .catch((ex) => {
        console.log(ex)
        toast.error(ex.response.data.message)
        stopLoading()
      })
  }

  const handleLinkClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    const profileLinkClicked = e.currentTarget.id.includes("profile")
    if (profileLinkClicked) {
      if (profileAccessStatus === "blocked") {
        e.preventDefault()
        setTryProfileFeature(true)
      }
    }
  }

  const acceptCodeOfEthics = () => {
    updateNextStepsData(false, "approved").then(() => {
      const url = `https://zoee-downloads-${process.env.REACT_APP_USER_ENVIRONMENT}.s3.amazonaws.com/CodeOfEthicsZoee.pdf`
      window.open(url, "_blank")
      navigate("/coach/edit-website", {
        state: { code_of_ethics_accepted: true },
      })
    })
  }

  useScrollToTop()

  useEffect(() => {
    getCoachPrivateProfile()
    setUserActiveSubscription()
  }, [])

  useEffect(() => {
    getNextStepsValues()
    if (location?.state?.verifyPhone === true) {
      sendVerifyPhone()
    } else if (location?.state?.violated_profile_access) {
      toast.error(
        `Please accept Code of Ethics to get access to this feature. It's currently in beta.`
      )
    }
  }, [profileAccessStatus])

  useEffect(() => {
    const link = document.createElement("link")
    link.href = "https://assets.calendly.com/assets/external/widget.css"
    link.rel = "stylesheet"
    document.head.appendChild(link)

    const script = document.createElement("script")
    script.src = "https://assets.calendly.com/assets/external/widget.js"
    script.async = true
    document.head.appendChild(script)

    return () => {
      document.head.removeChild(link)
      document.head.removeChild(script)
    }
  }, [])

  const initializeCalendly = () => {
    const isTrialExpired = new Date(user.trialExpiration) < new Date()
    const url =
      activeSubscription?.subscription_end_date || isTrialExpired
        ? "https://calendly.com/zoeeapp/group-orientation"
        : "https://calendly.com/zoeeapp/personal-zoee-discovery-session-clone"
    window.Calendly.initPopupWidget({ url })
  }

  return (
    <>
      {/* Modals */}
      <OnboardingCallModal />
      {tryProfileFeature && (
        <CodeOfEthicsModal
          setShowModal={setTryProfileFeature}
          confirmCallback={acceptCodeOfEthics}
        />
      )}

      {isModalOpen && (
        <>
          <link
            href="https://assets.calendly.com/assets/external/widget.css"
            rel="stylesheet"
          />
          <script
            src="https://assets.calendly.com/assets/external/widget.js"
            type="text/javascript"
            async
          ></script>
        </>
      )}

      {/* Begin Page */}

      <div className="container max-w-screen-xl">
        <div className="flex flex-col justify-between gap-x-10 md:flex-row">
          <h1 className="mb-2 text-3xl font-bold md:text-5xl">
            Welcome to Zoee, {user?.firstName ? user?.firstName : user?.email}!
          </h1>

          {user.cohort_status !== "active" && (
            <div className="flex flex-col gap-2 md:items-center md:justify-center">
              <p className="font-bold">Not sure where to start?</p>
              <Button
                variant="outline"
                onClick={() => {
                  setIsModalOpen(!isModalOpen)
                  initializeCalendly()
                }}
              >
                <SvgPhone fill="#4750F5" />
                Schedule Onboarding Call
              </Button>
            </div>
          )}
        </div>
        <h2 className="mb-[8px] mt-6 text-lg font-bold text-black md:mt-0 md:text-xl lg:text-2xl">
          Start building your business in these easy steps:
        </h2>

        <ol className="mt-3 list-inside list-decimal font-bold md:mt-0">
          {/* <li>Verify Identity</li> */}
          <li>Sync Calendar</li>
          <li>Create and publish your first service</li>
        </ol>

        <p className="text-muted-foreground">
          When complete, view your service on "My Zoee Website"
        </p>

        <div className="mb-[60px] mt-[45px] grid grid-cols-12 gap-4">
          {!businessLinks.length ? (
            <div className="mx-auto">
              <Loader />
            </div>
          ) : (
            <>
              {businessLinks?.map((link, idx: number) => {
                return (
                  <BusinessLink
                    index={idx}
                    title={link.title}
                    text={link.text}
                    link={link.link}
                    key={idx}
                    complete={link.complete}
                    icon={link.icon}
                    linkId={link.linkId}
                    handleLinkClick={handleLinkClick}
                    profileAccessStatus={profileAccessStatus}
                    step={link.step}
                  />
                )
              })}
            </>
          )}
        </div>
      </div>

      <div className="mx-auto flex flex-col items-center px-[24px] pt-[12px] md:w-[1080px] md:px-0 md:pt-0">
        <div className="flex w-full flex-col"></div>
      </div>
    </>
  )
}

export default ManageBusiness
